import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart } from 'lucide-react';
import { useStoreData, getMainImage } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import ProductDetail from './ProductDetail';

const ProductCard = ({ product, saleSettings, onProductClick }) => {
  const [isLiked, setIsLiked] = useState(false);
  const mainImage = getMainImage(product);
  const isOutOfStock = product.stock === 0;
  const { addToCart } = useCart();

  // Calculate sale price if sale is enabled
  // Sale logic:
  // 1. Global sale must be enabled
  // 2. Product saleEnabled must be true (defaults to true if not set)
  // 3. Use product-specific percentage if set, otherwise use global percentage
  const globalSaleEnabled = saleSettings?.enabled && saleSettings?.percentage > 0;
  const productSaleEnabled = product.saleEnabled !== false; // Default to true if not set
  const salePercentage = product.salePercentage || saleSettings?.percentage || 0;

  const isSaleActive = globalSaleEnabled && productSaleEnabled && salePercentage > 0;
  const salePrice = isSaleActive
    ? Math.round(product.price - (product.price * salePercentage / 100))
    : product.price;

  const handleAddToCart = (e) => {
    e.stopPropagation(); // Prevent opening the product detail modal

    const productToAdd = {
      ...product,
      price: salePrice,
      originalPrice: product.price,
      saleDiscount: isSaleActive ? salePercentage : 0
    };

    addToCart(productToAdd, 1);
  };

  return (
    <div className="group cursor-pointer" onClick={() => onProductClick(product)}>
      <div className="relative overflow-hidden rounded-lg mb-4 bg-gray-100">
        <img
          src={mainImage}
          alt={product.name}
          className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-gray-800 px-4 py-2 rounded-full font-semibold">Out of Stock</span>
          </div>
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {isSaleActive && !isOutOfStock && (
            <div className="bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
              {saleSettings.label || 'SALE'} {salePercentage}% OFF
            </div>
          )}
          {product.newArrival && !isOutOfStock && (
            <div className="bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
              NEW ARRIVAL
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsLiked(!isLiked);
          }}
          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all duration-300 transform hover:scale-110"
        >
          <Heart
            size={20}
            className={`transition-colors ${
              isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'
            }`}
          />
        </button>
        {!isOutOfStock && (
          <button
            onClick={handleAddToCart}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#4a1a1f] text-white px-6 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 hover:bg-[#5a2a2f] flex items-center gap-2"
          >
            <ShoppingCart size={18} />
            <span className="text-sm uppercase tracking-wider">Add to Cart</span>
          </button>
        )}
      </div>
      <div className="text-center">
        <h3 className="text-lg font-medium mb-2 group-hover:text-[#4a1a1f] transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center justify-center gap-2">
          {isSaleActive ? (
            <>
              <p className="text-gray-400 line-through text-sm">PKR {product.price.toLocaleString()}</p>
              <p className="text-red-600 font-bold text-lg">PKR {salePrice.toLocaleString()}</p>
            </>
          ) : (
            <p className="text-[#4a1a1f] font-semibold text-lg">PKR {product.price.toLocaleString()}</p>
          )}
        </div>
        {product.stock > 0 && product.stock < 10 && (
          <p className="text-sm text-orange-600 mt-1">Only {product.stock} left in stock!</p>
        )}
      </div>
    </div>
  );
};

const ProductGrid = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('new-arrivals');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { products, categories, settings, loading } = useStoreData();

  // Sort categories by sequence
  const sortedCategories = [...categories].sort((a, b) => {
    const seqA = a.sequence ?? a.id;
    const seqB = b.sequence ?? b.id;
    return seqA - seqB;
  });

  // Handle navigation from header (New Arrivals click)
  useEffect(() => {
    if (location.state?.scrollToProducts && location.state?.selectNewArrivals) {
      setActiveCategory('new-arrivals');
      const element = document.getElementById('trending-outfits');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Listen for custom event from header
  useEffect(() => {
    const handleSelectNewArrivals = () => {
      setActiveCategory('new-arrivals');
    };
    window.addEventListener('selectNewArrivals', handleSelectNewArrivals);
    return () => window.removeEventListener('selectNewArrivals', handleSelectNewArrivals);
  }, []);

  // Filter products and limit to 6 for homepage
  const filteredProducts = activeCategory === 'new-arrivals'
    ? products.filter(p => p.newArrival === true).slice(0, 6)
    : products.filter(p => p.category === activeCategory).slice(0, 6);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseDetail = () => {
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto text-center">
          <p className="text-gray-600">Loading products...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="trending-outfits" className="py-16 px-4 bg-gray-50">
      <div className="container mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4 uppercase tracking-wider">Trending Outfits</h2>
        <p className="text-center text-gray-600 mb-8">Discover our most popular abayas</p>

        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setActiveCategory('new-arrivals')}
            className={`px-6 py-2 rounded-full text-sm uppercase tracking-wider transition-all ${
              activeCategory === 'new-arrivals'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            New Arrivals
          </button>
          {sortedCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.slug)}
              className={`px-6 py-2 rounded-full text-sm uppercase tracking-wider transition-all ${
                activeCategory === category.slug
                  ? 'bg-[#4a1a1f] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              saleSettings={settings?.sale}
              onProductClick={handleProductClick}
            />
          ))}
        </div>

        {/* View All Button */}
        {filteredProducts.length === 6 && (
          <div className="text-center mt-12">
            <button
              onClick={() => navigate(`/category/${activeCategory}`)}
              className="bg-[#4a1a1f] text-white px-8 py-3 rounded-full hover:bg-[#5a2a2f] transition-colors uppercase tracking-wider font-semibold"
            >
              View All {activeCategory === 'new-arrivals' ? 'New Arrivals' : sortedCategories.find(c => c.slug === activeCategory)?.name}
            </button>
          </div>
        )}
      </div>

      {/* Product Detail Modal */}
      <ProductDetail
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={handleCloseDetail}
        saleSettings={settings?.sale}
      />
    </section>
  );
};

export default ProductGrid;